import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal } from 'antd';
import './plan-limits-explainer.css';
import './plan-limit-field-guide.css';

/**
 * Field-by-field walkthrough of the Add/Edit Limit form (see
 * PlanLimitsManager.jsx). Every example is grounded in one invented plan —
 * Horizon Health's Sapphire Plan — built the same way a real Aman HMO
 * benefit schedule reads (see the PDF benefit-schedule appendix this was
 * modelled on), so the numbers are fictitious but the *shape* of every
 * example — envelope sizes, sub-caps, ward/ICU metering, eligibility —
 * mirrors a real plan design. Shares the ple-* visual language and
 * slide/dot-nav mechanic with PlanLimitsExplainerModal so the two read as
 * one family, not two unrelated UIs.
 */

const SLIDES = [
  {
    tone: 'live',
    badge: '✎ Field guide',
    headline: <>One fictitious plan, every field <em>explained</em>.</>,
    body: 'Every field below is grounded in one worked example: Horizon Health’s Sapphire Plan — a private, family-of-six plan built the same way a real Aman HMO benefit schedule reads, just invented for this walkthrough. Follow it end to end and you’ll see exactly how a benefit-schedule row becomes a row in Limits & Enforcement.',
    visual: (
      <div className="pfg-schedule">
        <div className="pfg-schedule__title">HORIZON HEALTH <span>— Sapphire Plan (fictitious)</span></div>
        <div className="pfg-schedule__row">
          <span className="pfg-schedule__cell"><b>In-patient Limit</b><em>₦1,800,000 / policy / year</em></span>
          <span className="pfg-schedule__cell"><b>Out-patient Limit</b><em>₦450,000 / policy / year</em></span>
          <span className="pfg-schedule__cell"><b>Family size</b><em>6 (1 + 1 + 4)</em></span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '① Name (optional)',
    headline: <>Give the row a name, or let it <em>introduce itself</em>.</>,
    body: 'Every row in the Limits Definitions list used to read as a bare "category limit" — impossible to tell apart from any other category limit on the same plan. Type a name here and it’s shown verbatim. Leave it blank and the system builds one for you from the metric, target, and period — so a row is never nameless, just sometimes auto-named.',
    visual: (
      <div className="pfg-compare">
        <div className="pfg-compare__col">
          <span className="pfg-compare__tag">You typed a name</span>
          <div className="pfg-compare__value">Chronic Disease Medication Cap</div>
        </div>
        <div className="pfg-compare__col pfg-compare__col--muted">
          <span className="pfg-compare__tag">Left blank → auto-labeled</span>
          <div className="pfg-compare__value">Amount · Private Ward · annual</div>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '② Limit type',
    headline: <>Choose what the cap <em>actually measures</em>.</>,
    body: 'Sapphire’s Chronic Disease Medication cap measures Amount — ₦85,000. Its Private Ward cap measures Item Quantity — the exact count of ward-night units billed on tagged claim lines, not an approximation of admission-to-discharge dates (that’s the older Bed Nights metric, and it over-counts anyone who changes ward mid-stay). Wellness Gym measures Visit Count. Pick the metric that matches what the schedule actually caps.',
    visual: (
      <div className="pfg-metric-grid">
        <div className="pfg-metric pfg-metric--on"><span className="pfg-metric__name">Amount</span><span className="pfg-metric__unit">₦</span><span className="pfg-metric__note">Chronic Disease Medication</span></div>
        <div className="pfg-metric pfg-metric--on"><span className="pfg-metric__name">Item Quantity</span><span className="pfg-metric__unit">units</span><span className="pfg-metric__note">Private Ward — exact</span></div>
        <div className="pfg-metric pfg-metric--warn"><span className="pfg-metric__name">Bed Nights</span><span className="pfg-metric__unit">nights</span><span className="pfg-metric__note">whole-stay approximation</span></div>
        <div className="pfg-metric pfg-metric--on"><span className="pfg-metric__name">Visit Count</span><span className="pfg-metric__unit">visits</span><span className="pfg-metric__note">Wellness Gym</span></div>
        <div className="pfg-metric"><span className="pfg-metric__name">Sessions</span><span className="pfg-metric__unit">sessions</span><span className="pfg-metric__note">e.g. Physiotherapy</span></div>
        <div className="pfg-metric"><span className="pfg-metric__name">Item Count</span><span className="pfg-metric__unit">items</span><span className="pfg-metric__note">individual claim lines</span></div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '③ Scope',
    headline: <>Is the pool shared by the family, or <em>private to each person</em>?</>,
    body: 'Sapphire’s ₦1,800,000 In-patient envelope is Policy-scoped — the whole family of six draws from one pool, the same way the family premium implies one shared cover. Its ₦40,000 Dental Care cap is Enrollee-scoped — every one of the six gets their own ₦40,000, not a shared pot six people compete for.',
    visual: (
      <div className="pfg-scope">
        <div className="pfg-scope__col">
          <span className="pfg-scope__label">Policy — shared</span>
          <div className="pfg-scope__pool">
            {['P', 'S', 'C1', 'C2', 'C3', 'C4'].map((m) => <span key={m} className="pfg-scope__avatar">{m}</span>)}
          </div>
          <span className="pfg-scope__caption">₦1,800,000 · one bucket</span>
        </div>
        <div className="pfg-scope__col">
          <span className="pfg-scope__label">Enrollee — private</span>
          <div className="pfg-scope__pool pfg-scope__pool--split">
            {['P', 'S', 'C1', 'C2', 'C3', 'C4'].map((m) => (
              <span key={m} className="pfg-scope__avatar pfg-scope__avatar--own">{m}<em>₦40k</em></span>
            ))}
          </div>
          <span className="pfg-scope__caption">₦40,000 × 6 · six buckets</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '④ Target type',
    headline: <>Plan-wide, a whole category, or <em>one named benefit</em>.</>,
    body: 'Three levels, the same shape as the schedule itself. Plan targets the ₦1,800,000 In-patient envelope directly. Category targets a costable_category like "Outpatient Care" for the ₦450,000 envelope. Standard Item Type targets one named benefit inside it — Chronic Disease Medication, Private Ward, ICU/HDU — the same bounded, admin-curated taxonomy used to tag real tariff items, so the limit always resolves against real billing lines.',
    visual: (
      <div className="pfg-nest">
        <div className="pfg-nest__ring pfg-nest__ring--plan">
          <span className="pfg-nest__tag">Plan</span>
          <span className="pfg-nest__val">₦1,800,000 in-patient envelope</span>
          <div className="pfg-nest__ring pfg-nest__ring--category">
            <span className="pfg-nest__tag">Category</span>
            <span className="pfg-nest__val">₦450,000 Outpatient Care</span>
            <div className="pfg-nest__ring pfg-nest__ring--item">
              <span className="pfg-nest__tag">Standard Item Type</span>
              <span className="pfg-nest__val">₦85,000 Chronic Disease Medication</span>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '⑤ Period',
    headline: <>How often the counter <em>resets</em>.</>,
    body: 'Most of Sapphire’s caps are Annual, resetting once a year like the schedule’s own "/Annum" benefits. Wellness Gym is Monthly — 3 visits reset every month, not once a year, matching a real wellness benefit’s "times per month" wording exactly. Quarterly and Per encounter exist for anything that resets on a different clock.',
    visual: (
      <div className="ple-chip-row">
        <span className="ple-chip ple-chip--on">Annual <em>— most of Sapphire’s caps</em></span>
        <span className="ple-chip ple-chip--on">Monthly <em>— Wellness Gym</em></span>
        <span className="ple-chip">Quarterly</span>
        <span className="ple-chip">Per encounter</span>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '⑥ Limit value',
    headline: <>The actual number this cap <em>enforces</em>.</>,
    body: 'Paired with whatever metric you picked above it: 85,000 for an Amount cap, 25 for an Item Quantity cap measured in ward-nights, 3 for a Visit Count cap measured in monthly gym visits. The unit shown next to the field always matches the metric — you’re never guessing whether a number means naira, nights, or visits.',
    visual: (
      <div className="ple-def-card">
        <div className="ple-def-cell"><span className="ple-def-cell__label">Chronic Disease Medication</span><span className="ple-def-cell__value">₦85,000</span></div>
        <div className="ple-def-cell"><span className="ple-def-cell__label">Private Ward</span><span className="ple-def-cell__value">25 <small>nights</small></span></div>
        <div className="ple-def-cell"><span className="ple-def-cell__label">Wellness Gym</span><span className="ple-def-cell__value">3 <small>visits/mo</small></span></div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '⑦ Check-in type (optional)',
    headline: <>Anchor a limit to the <em>right kind of visit</em>.</>,
    body: 'Sapphire’s Private Ward and ICU/HDU caps only make sense against an Inpatient admission — leaving this blank would let a miscoded outpatient encounter accidentally count against a ward-night cap. Setting Check-in type to Inpatient closes that gap. Leave it blank for benefits that apply regardless of visit type, like the overall Outpatient envelope.',
    visual: (
      <div className="ple-flow">
        <div className="ple-flow__step pfg-flow__step--muted">
          <span className="ple-flow__step-label">Outpatient visit</span>
          <span className="ple-flow__step-value">skipped</span>
        </div>
        <span className="ple-flow__arrow">·</span>
        <div className="ple-flow__step ple-flow__step--hit">
          <span className="ple-flow__step-label">Inpatient admission</span>
          <span className="ple-flow__step-value">counted against Private Ward</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '⑧ Priority',
    headline: <>Which limit gets checked — and shown — <em>first</em>.</>,
    body: 'When more than one limit applies to the same claim — Sapphire’s ₦85,000 medication cap and its ₦450,000 outpatient envelope both apply to the same prescription — Priority decides evaluation and display order, 0 first. It doesn’t decide which one "wins": every applicable limit is still checked independently, and a claim must clear all of them.',
    visual: (
      <div className="pfg-priority">
        <div className="pfg-priority__row"><span className="pfg-priority__num">0</span>Chronic Disease Medication — ₦85,000<span className="pfg-priority__check">must clear</span></div>
        <div className="pfg-priority__row"><span className="pfg-priority__num">10</span>Outpatient Care envelope — ₦450,000<span className="pfg-priority__check">must clear</span></div>
      </div>
    ),
  },
  {
    tone: 'problem',
    badge: '⚠ Parent limit ID & Cascade group',
    headline: <>Organize the list — <em>don’t expect a shared pool</em>.</>,
    body: 'Setting the ₦85,000 medication cap’s Parent limit ID to the ₦450,000 outpatient envelope nests it visually underneath in the list — easier to scan, nothing more. Tagging both with the same Cascade group ("drugs") groups them for a human reading the list. Neither field moves money between limits — spending against the medication cap doesn’t reduce the envelope’s remaining balance in any special way. Every row is still evaluated entirely on its own.',
    visual: (
      <div className="pfg-no-cascade">
        <div className="pfg-no-cascade__bar">Chronic Disease Medication <em>₦85,000</em></div>
        <span className="pfg-no-cascade__x">✕ no money moves between these</span>
        <div className="pfg-no-cascade__bar">Outpatient Care envelope <em>₦450,000</em></div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '⑨ Eligibility (optional)',
    headline: <>Narrow <em>who</em> a limit applies to.</>,
    body: 'Sapphire’s Neonatal Care cap (₦120,000) only makes sense for dependents aged 0–1 — set Member scope to Dependents and Age max to 1, and it’s skipped entirely (not shown as "compliant") for anyone else. Its Antenatal + Delivery + Postnatal cap (₦220,000) only applies to female members. Leave all three blank, like most of Sapphire’s caps, and the limit simply applies to everyone — same as before eligibility existed.',
    visual: (
      <div className="ple-chip-row">
        <span className="ple-chip ple-chip--on">Dependents only</span>
        <span className="ple-chip ple-chip--on">Ages 0–1</span>
        <span className="ple-chip ple-chip--on">Female</span>
        <span className="ple-chip">All enrollees <em>— the default</em></span>
      </div>
    ),
  },
  {
    tone: 'closing',
    badge: '★ Sapphire Plan, assembled',
    headline: <>That’s the whole form — <em>one plan, fully defined</em>.</>,
    body: 'Every field above, applied together. Eleven real-world lines, eleven fields, one coherent plan — exactly what the real Aman HMO benefit schedule this was modelled on does for every tier, row by row.',
    visual: (
      <div className="ple-recap">
        <div className="ple-recap__row"><span className="ple-recap__num">01</span>In-patient envelope — Plan · Amount · ₦1,800,000 · Policy · annual</div>
        <div className="ple-recap__row"><span className="ple-recap__num">02</span>Outpatient envelope — Category · Amount · ₦450,000 · Policy · annual</div>
        <div className="ple-recap__row"><span className="ple-recap__num">03</span>Chronic Disease Medication — Standard Item Type · ₦85,000 · sub-limit of 02</div>
        <div className="ple-recap__row"><span className="ple-recap__num">04</span>Private Ward — Standard Item Type · 25 nights · Inpatient only</div>
        <div className="ple-recap__row"><span className="ple-recap__num">05</span>ICU / HDU — Standard Item Type · Item Quantity · Inpatient only</div>
        <div className="ple-recap__row"><span className="ple-recap__num">06</span>Neonatal Care — ₦120,000 · dependents, age 0–1</div>
        <div className="ple-recap__row"><span className="ple-recap__num">07</span>Antenatal + Delivery + Postnatal — ₦220,000 · female members</div>
        <div className="ple-recap__row"><span className="ple-recap__num">08</span>Wellness Gym — Visit Count · 3 · Enrollee · monthly</div>
      </div>
    ),
  },
];

export const PlanLimitFieldGuideModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const goTo = useCallback((next) => {
    setStep((current) => {
      const clamped = Math.max(0, Math.min(SLIDES.length - 1, next));
      return clamped === current ? current : clamped;
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goTo(step + 1);
      else if (e.key === 'ArrowLeft') goTo(step - 1);
      else if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, goTo, onClose]);

  const slide = useMemo(() => SLIDES[step], [step]);
  const isFirst = step === 0;
  const isLast = step === SLIDES.length - 1;

  return (
    <Modal
      className="ple-modal pfg-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={780}
      destroyOnClose
      maskClosable
      centered
    >
      <div className="ple-stage" data-tone={slide.tone}>
        <div className="ple-glow" aria-hidden />

        <div className="ple-topbar">
          <span className="ple-brand">
            <span className="ple-brand__dot" aria-hidden />
            Limit form, field by field
          </span>
          <button type="button" className="ple-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ple-body">
          <div className="ple-slide" key={step}>
            <span className="ple-badge">
              <span className="ple-badge__icon" aria-hidden>{slide.badge.slice(0, 1)}</span>
              {slide.badge.slice(2)}
            </span>
            <h2 className="ple-headline">{slide.headline}</h2>
            <p className="ple-body-text">{slide.body}</p>
            <div className="ple-visual">{slide.visual}</div>
          </div>
        </div>

        <div className="ple-nav">
          <div className="ple-dots" role="tablist" aria-label="Slides">
            {SLIDES.map((s, i) => (
              <button
                key={String(i)}
                type="button"
                role="tab"
                aria-selected={i === step}
                aria-label={`Go to slide ${i + 1}`}
                className={`ple-dot${i === step ? ' ple-dot--active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
          <div className="ple-nav__buttons">
            <span className="ple-step-count">{String(step + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}</span>
            <button type="button" className="ple-btn" onClick={() => goTo(step - 1)} disabled={isFirst}>
              Back
            </button>
            {isLast ? (
              <button type="button" className="ple-btn ple-btn--primary" onClick={onClose}>
                Done
              </button>
            ) : (
              <button type="button" className="ple-btn ple-btn--primary" onClick={() => goTo(step + 1)}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlanLimitFieldGuideModal;
